import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { storage } from '../../lib/firebase'

export default function ImageUploader({
  value,
  onChange,
  folder = 'images',
  accept = { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
  compact = false
}) {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    setUploading(true)
    setProgress(0)
    setError(null)

    try {
      const timestamp = Date.now()
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const storageRef = ref(storage, `${folder}/${timestamp}-${safeName}`)

      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on('state_changed',
        (snapshot) => {
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          setProgress(pct)
        },
        (err) => {
          setError(err.message)
          setUploading(false)
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          onChange(downloadURL)
          setUploading(false)
        }
      )
    } catch (err) {
      setError(err.message)
      setUploading(false)
    }
  }, [folder, onChange])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    disabled: uploading
  })

  const handleClear = () => {
    onChange('')
    setError(null)
  }

  // Compact mode: just show dropzone, parent handles preview
  if (compact) {
    return (
      <div className="image-uploader compact">
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span>{Math.round(progress)}%</span>
            </div>
          ) : isDragActive ? (
            <p>Drop here</p>
          ) : (
            <p>Drop or click</p>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
    )
  }

  return (
    <div className="image-uploader">
      {value ? (
        <div className="image-preview">
          <img src={value} alt="Preview" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleClear}
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span>{Math.round(progress)}%</span>
            </div>
          ) : isDragActive ? (
            <p>Drop the image here...</p>
          ) : (
            <p>Drag & drop an image, or click to select</p>
          )}
        </div>
      )}
      {error && <p className="error-text">{error}</p>}
    </div>
  )
}
